/**
 * World generation — spec §6. Seeded and deterministic: same seed, same planet.
 *
 * Correlation (spec §6 rule 3) is implemented as a latent factor model rather
 * than as pairwise fudging of independent noise maps. Three factors stand in for
 * the geology the spec describes:
 *
 *   A — ore systems: banded iron and sulfides, with quartz as ubiquitous gangue
 *   B — sedimentary / evaporite: carbonate, apatite, sylvite
 *   C — ice: concentrates where rock does not
 *
 * Every mineral is a weighted mix of the three plus its own idiosyncratic noise.
 * All five couplings §6 asks for fall out of the loadings, and they stay
 * mutually consistent — which pairwise adjustment does not, since correcting one
 * pair perturbs the others. It also gives hexes the readable *characters* §6
 * wants: an ore hex, an evaporite hex, an ice hex.
 */

import {
  STARTING_SCAVENGE_BP,
  WORLD_SUPPLY_MAX,
  WORLD_SUPPLY_MIN,
} from './constants';
import { ELEMENT_IDS, MINERAL_IDS, type ElementId, type MineralId } from './data';
import {
  elementsFromMinerals,
  targetMineralShareBp,
  viableCityElementDemand,
} from './demand';
import { hexKey, parseHexKey, ringOf, sortedHexKeys, worldHexes } from './hex';
import { createRng, cursor } from './rng';
import type { HexState, PlanetState, RngState } from './types';

/** Spec §6 rule 5 rejects unsolvable seeds. This bounds the search. */
export const MAX_GENERATION_ATTEMPTS = 100;

/**
 * Deposits scoring below this are zeroed, so hexes have genuine gaps rather
 * than trace amounts of everything (spec §6 rule 2).
 *
 * Scores are centred on 20000 with a standard deviation near 4500, because a
 * weighted average of factors is much narrower than the factors themselves.
 * 17500 sits about half a standard deviation below centre and zeroes roughly a
 * third of deposits — enough that hexes have real gaps. A floor set by eye from
 * the 0..40000 range instead of from the actual spread zeroes almost nothing.
 */
const DEPOSIT_FLOOR = 17500;

/**
 * Planetary abundance range, in basis points.
 *
 * Spec §6 says "roughly 0.2x to 3x", applied to a shared base. That range is
 * survivable when it varies how much of a mineral there is around a mix that
 * already works; it is not survivable as the thing that DETERMINES the mix. At
 * 0.2x, a single-source mineral like quartz leaves the colony with no silicon at
 * any rate it can use, and no amount of mining fixes it.
 *
 * Narrowed to 0.5x-2.0x, applied on top of the demand-derived shares below. A
 * world can still be quartz-poor or iron-rich enough to change how it plays; it
 * can no longer be missing something the factory cannot do without.
 * See SPEC-ERRATA.md E19.
 */
const BIAS_MIN_BP = 5000;
const BIAS_MAX_BP = 20000;

/**
 * THE LANDING SITE WAS CHOSEN, NOT LANDED ON BLIND.
 *
 * Spec §6 draws every hex from one distribution, home included, so the colony
 * can set down on ground it would obviously have rejected from orbit and then
 * spend the session unable to build anything within reach.
 *
 * The disqualifying property is HOMOGENEITY. A site made mostly of one mineral
 * yields mostly one set of elements, and the factory cannot synthesise its way
 * out of that — an ice field is the obvious case, but a pure evaporite flat or
 * an iron field alone is just as useless. Being merely poor is a lesser problem
 * and is handled separately.
 *
 * Richness is nudged; suitability is enforced by rejection. That split is the
 * honest model of a survey: it does not bend the geology, it rules sites out.
 * Ring 2 is untouched either way — it is what the colony reaches into once the
 * good ground nearby runs down, and where the planet's real character shows, so
 * a chosen start does not flatten the variation §6 exists to create.
 *
 * See SPEC-ERRATA.md E18.
 */
const LANDING_ENDOWMENT_BONUS: readonly number[] = [7000, 4500, 0];

/**
 * Minimum share of the area's supply within reach at the start — the home hex
 * plus ring 1.
 *
 * Those seven hexes are 7/19 of the map, so an even spread would be about 37%.
 * 30% rejects a genuinely poor neighbourhood while still letting the outer ring
 * hold most of the material.
 */
const MIN_LOCAL_SUPPLY_FRACTION = 30;

/**
 * No single mineral may exceed this share of the landing area's supply.
 *
 * The homogeneity test. An even spread across eight minerals is 12.5% each; 40%
 * leaves room for a site with real character — ore-dominated, say — while
 * rejecting the one-mineral fields a survey would have skipped.
 */
const MAX_LOCAL_MINERAL_SHARE = 40;

/** Distinct minerals that must be present locally, of the eight. */
const MIN_LOCAL_MINERAL_VARIETY = 6;

/** The rock-formers early construction cannot proceed without. */
const ESSENTIAL_LOCAL_ELEMENTS: readonly ElementId[] = ['Fe', 'Si', 'Ca', 'C', 'O'];

interface Loading {
  ore: number;
  sediment: number;
  ice: number;
  /**
   * Overall endowment of the hex, loading positively on everything.
   *
   * Without it the three geological axes are independent, so a hex rich in ore
   * is typically middling in sediment, and totalling eight minerals averages
   * the variation away — every hex ends up worth about the same, which is
   * exactly what spec §6 says the world must not be. This factor is what makes
   * a hex rich or barren as a whole.
   */
  endowment: number;
  idiosyncratic: number;
}

/** Factor loadings, per cent of the composite score. Each row sums to 100. */
const LOADINGS: Record<MineralId, Loading> = {
  // Banded iron: the anchor of the ore factor.
  hematite: { ore: 60, sediment: 0, ice: -15, endowment: 25, idiosyncratic: 20 },
  // Ubiquitous gangue, so it tracks ore but not as tightly.
  quartz: { ore: 45, sediment: 5, ice: -15, endowment: 25, idiosyncratic: 25 },
  bauxite: { ore: 20, sediment: 10, ice: -15, endowment: 25, idiosyncratic: 35 },
  // Sulfide systems co-occur with banded iron.
  chalcopyrite: { ore: 55, sediment: 0, ice: -15, endowment: 25, idiosyncratic: 25 },
  // Ice concentrates where rock does not, so it takes only a weak share of the
  // general endowment and is dominated by its own factor.
  waterIce: { ore: -25, sediment: -15, ice: 70, endowment: 15, idiosyncratic: 25 },
  carbonate: { ore: 0, sediment: 60, ice: -10, endowment: 25, idiosyncratic: 25 },
  // Sedimentary/marine, alongside carbonate.
  apatite: { ore: 0, sediment: 55, ice: -10, endowment: 25, idiosyncratic: 30 },
  // Evaporite: with carbonate, against ice.
  sylvite: { ore: 0, sediment: 45, ice: -35, endowment: 20, idiosyncratic: 25 },
};

export interface WorldGenResult {
  planet: PlanetState;
  /** Home hex material, scavenged flat on landing. spec §6 rule 4 */
  startingMinerals: Record<MineralId, number>;
  planetaryBiasBp: Record<MineralId, number>;
  totalMineralUnits: number;
  attempts: number;
  rng: RngState;
}

/**
 * Approximately normal, centred on zero, range about ±20000.
 *
 * Irwin-Hall: the sum of four uniforms. Box-Muller would be the textbook choice
 * but needs Math.log and Math.cos, which are banned in the sim because they are
 * not correctly-rounded (see SPEC-ERRATA.md E6). Four uniforms is plenty of
 * bell for deposit noise.
 */
function drawFactor(draw: () => number): number {
  return draw() + draw() + draw() + draw() - 20000;
}

function zeroMinerals(): Record<MineralId, number> {
  const out = {} as Record<MineralId, number>;
  for (const id of MINERAL_IDS) out[id] = 0;
  return out;
}

/** One candidate world. May or may not be solvable. */
function generateCandidate(rng: RngState): {
  deposits: Record<string, Record<MineralId, number>>;
  planetaryBiasBp: Record<MineralId, number>;
  total: number;
  rng: RngState;
} {
  const c = cursor(rng);
  const uniform = (): number => c.int(0, 9999);

  const planetaryBiasBp = {} as Record<MineralId, number>;
  for (const id of MINERAL_IDS) planetaryBiasBp[id] = c.biasBp(BIAS_MIN_BP, BIAS_MAX_BP);

  const hexes = worldHexes();
  const raw: Record<string, Record<MineralId, number>> = {};

  for (const coord of hexes) {
    const ore = drawFactor(uniform);
    const sediment = drawFactor(uniform);
    const ice = drawFactor(uniform);
    // The site was surveyed and chosen, so the ground near home is better than
    // average. Ring 2 keeps the area's unmodified character.
    const endowment = drawFactor(uniform) + (LANDING_ENDOWMENT_BONUS[ringOf(coord)] ?? 0);

    const perMineral = zeroMinerals();
    for (const id of MINERAL_IDS) {
      const loading = LOADINGS[id];
      const idiosyncratic = drawFactor(uniform);
      const composite =
        (ore * loading.ore +
          sediment * loading.sediment +
          ice * loading.ice +
          endowment * loading.endowment +
          idiosyncratic * loading.idiosyncratic) /
        100;

      // Shift to 0..40000, then cut the low tail away entirely.
      const score = composite + 20000;
      if (score < DEPOSIT_FLOOR) continue;

      const value = Math.floor(((score - DEPOSIT_FLOOR) * planetaryBiasBp[id]) / 10000);
      perMineral[id] = value;
    }
    raw[hexKey(coord)] = perMineral;
  }

  // Scale to a target COMPOSITION, not just a target total.
  //
  // Spec §6 normalises the grand total and lets the noise decide the mix. That
  // is what left silicon, carbon, copper and potassium at zero while oxygen and
  // hydrogen piled up unused: those four have exactly one source mineral each,
  // so if the noise gives that mineral a small share, the element is
  // unobtainable at any useful rate no matter how much material is in the
  // ground.
  //
  // Each mineral is normalised to its own target instead — demand-derived share
  // times this world's abundance bias. Per-hex pattern and the §6 correlations
  // survive untouched, because normalising a mineral scales every hex's value
  // for it by the same factor; what changes is only how much of each there is
  // area-wide.
  const target = c.int(WORLD_SUPPLY_MIN, WORLD_SUPPLY_MAX);
  const baseShareBp = targetMineralShareBp();

  let weightedTotal = 0;
  const weightBp = {} as Record<MineralId, number>;
  for (const id of MINERAL_IDS) {
    weightBp[id] = (baseShareBp[id] * planetaryBiasBp[id]) / 10000;
    weightedTotal += weightBp[id];
  }

  const perMineralTarget = zeroMinerals();
  if (weightedTotal > 0) {
    for (const id of MINERAL_IDS) {
      perMineralTarget[id] = Math.round((weightBp[id] * target) / weightedTotal);
    }
  }

  // Raw column sums, so each mineral can be scaled to its own target.
  const rawPerMineral = zeroMinerals();
  for (const key of Object.keys(raw).sort()) {
    const hex = raw[key]!;
    for (const id of MINERAL_IDS) rawPerMineral[id] += hex[id];
  }

  const scaled: Record<string, Record<MineralId, number>> = {};
  const scaledPerMineral = zeroMinerals();
  let scaledTotal = 0;

  const orderedKeys = Object.keys(raw).sort();
  for (const key of orderedKeys) {
    const source = raw[key]!;
    const out = zeroMinerals();
    for (const id of MINERAL_IDS) {
      const value =
        rawPerMineral[id] === 0
          ? 0
          : Math.round((source[id] * perMineralTarget[id]) / rawPerMineral[id]);
      out[id] = value;
      scaledPerMineral[id] += value;
      scaledTotal += value;
    }
    scaled[key] = out;
  }

  // Absorb per-mineral rounding drift into the hex that already holds the most
  // of that mineral, so a correction never creates a deposit where the noise
  // said there was none.
  for (const id of MINERAL_IDS) {
    const drift = perMineralTarget[id] - scaledPerMineral[id];
    if (drift === 0) continue;

    let bestKey = '';
    let bestValue = -1;
    for (const key of orderedKeys) {
      const value = scaled[key]![id];
      if (value > bestValue) {
        bestValue = value;
        bestKey = key;
      }
    }
    if (bestKey === '' || bestValue <= 0) continue;

    const corrected = Math.max(0, scaled[bestKey]![id] + drift);
    scaledTotal += corrected - scaled[bestKey]![id];
    scaled[bestKey]![id] = corrected;
  }

  return { deposits: scaled, planetaryBiasBp, total: scaledTotal, rng: c.commit() };
}

/** Spec §6 rule 5: can this world actually support a viable city? */
export function isSolvable(totalMinerals: Record<MineralId, number>): boolean {
  const supply = elementsFromMinerals(totalMinerals);
  const demand = viableCityElementDemand();
  for (const id of ELEMENT_IDS) {
    if (supply[id] < demand[id]) return false;
  }
  return true;
}

/**
 * Would a survey have chosen this spot?
 *
 * Three ways to fail: too poor to start, too homogeneous to work with, or
 * missing an element construction cannot do without. The last two are the same
 * objection at different resolutions — a site made mostly of one mineral yields
 * mostly one set of elements.
 */
export function hasViableLandingSite(
  deposits: Record<string, Record<MineralId, number>>,
): boolean {
  const local = zeroMinerals();
  let localTotal = 0;
  let areaTotal = 0;

  for (const key of Object.keys(deposits).sort()) {
    const hex = deposits[key]!;
    const isLocal = ringOf(parseHexKey(key)) <= 1;
    for (const id of MINERAL_IDS) {
      areaTotal += hex[id];
      if (isLocal) {
        local[id] += hex[id];
        localTotal += hex[id];
      }
    }
  }

  if (areaTotal <= 0 || localTotal <= 0) return false;
  if ((localTotal * 100) / areaTotal < MIN_LOCAL_SUPPLY_FRACTION) return false;

  let present = 0;
  for (const id of MINERAL_IDS) {
    if (local[id] <= 0) continue;
    present += 1;
    if ((local[id] * 100) / localTotal > MAX_LOCAL_MINERAL_SHARE) return false;
  }
  if (present < MIN_LOCAL_MINERAL_VARIETY) return false;

  const localElements = elementsFromMinerals(local);
  for (const id of ESSENTIAL_LOCAL_ELEMENTS) {
    if (localElements[id] <= 0) return false;
  }

  return true;
}

export function generateWorld(seed: number): WorldGenResult {
  let rng = createRng(seed);

  for (let attempt = 1; attempt <= MAX_GENERATION_ATTEMPTS; attempt++) {
    const candidate = generateCandidate(rng);
    rng = candidate.rng;

    const totals = zeroMinerals();
    for (const key of Object.keys(candidate.deposits).sort()) {
      const hex = candidate.deposits[key]!;
      for (const id of MINERAL_IDS) totals[id] += hex[id];
    }

    if (!isSolvable(totals)) continue;
    if (!hasViableLandingSite(candidate.deposits)) continue;

    const hexes: Record<string, HexState> = {};
    const home = hexKey({ q: 0, r: 0 });
    const startingMinerals = zeroMinerals();

    for (const coord of worldHexes()) {
      const key = hexKey(coord);
      const generated = candidate.deposits[key]!;
      const isHome = key === home;

      if (isHome) {
        // Spec §6 rule 4: the factory scavenged the home hex flat on landing.
        // Only a fraction of it is recovered — see STARTING_SCAVENGE_BP. The hex
        // is spent either way, and initialDeposits keeps the pre-scavenge
        // figures so it reads as fully depleted rather than empty-and-untouched.
        for (const id of MINERAL_IDS) {
          startingMinerals[id] = Math.floor((generated[id] * STARTING_SCAVENGE_BP) / 10000);
        }
      }

      hexes[key] = {
        coord: { q: coord.q, r: coord.r },
        ring: ringOf(coord) as 0 | 1 | 2,
        deposits: isHome ? zeroMinerals() : { ...generated },
        initialDeposits: { ...generated },
        // Spec §13 item 4 leaves surveying out of the PoC: full composition is
        // visible from the start.
        surveyed: true,
        assignedDrones: [],
      };
    }

    return {
      planet: {
        hexes,
        atmosphere: { density: 1, nitrogenFraction: 78 },
      },
      startingMinerals,
      planetaryBiasBp: candidate.planetaryBiasBp,
      totalMineralUnits: candidate.total,
      attempts: attempt,
      rng,
    };
  }

  throw new Error(
    `World generation failed to find a solvable planet for seed ${seed} in ` +
      `${MAX_GENERATION_ATTEMPTS} attempts. The generator is mis-tuned: either ` +
      `total supply is too low or the viable-city demand model is too demanding.`,
  );
}

/** Count of hexes that still hold anything. Used by the UI and by tests. */
export function minableHexCount(planet: PlanetState): number {
  let count = 0;
  for (const key of sortedHexKeys(planet.hexes)) {
    const hex = planet.hexes[key]!;
    if (MINERAL_IDS.some((id) => hex.deposits[id] > 0)) count += 1;
  }
  return count;
}

